import { yupResolver } from '@hookform/resolvers/yup';
import { JoinRule, Room } from 'matrix-js-sdk/src';
import { useForm } from 'react-hook-form';

import { getRoomMetadata } from 'modules/RoomsListItem/utils';
import { isPrivateRoom } from 'utils/matrixUtils';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import {
  currentTimeZoneOffset,
  getStartEndDate,
  extractTimeZone,
  getAndExtractAlias,
  RoomFormType,
  defaultReminders,
} from './utils';

interface UseCreateNewRoomFormProps {
  room?: Room;
  isSchedulingAvailable: boolean;
  isPrivateInitially?: boolean;
}

const useCreateNewRoomForm = ({ room, isSchedulingAvailable, isPrivateInitially }: UseCreateNewRoomFormProps) => {
  const initialAlias = getAndExtractAlias(room);
  const metadata = room && getRoomMetadata(room);
  const { initialStartAt, initialEndAt } = getStartEndDate(isSchedulingAvailable, metadata);
  const extractedTimeZone = metadata?.scheduledToStartAt ? extractTimeZone(metadata.scheduledToStartAt) : undefined;
  const initialTimeZone = typeof extractedTimeZone === 'number' ? extractedTimeZone : currentTimeZoneOffset;
  const initialIsPrivateRoom = room ? isPrivateRoom(room) : isPrivateInitially;

  const roomForm = useForm<RoomFormType>({
    mode: 'all',
    resolver: yupResolver(Schemas.getCreateNewRoom()),
    defaultValues: {
      roomName: room?.name || '',
      roomAlias: initialAlias || '',
      isWaitingListEnabled: room ? room.getJoinRule() === JoinRule.Knock : !!isPrivateInitially,
      isPrivateRoom: initialIsPrivateRoom,
      startDate: initialStartAt,
      startTime: initialStartAt,
      endDate: initialEndAt,
      endTime: initialEndAt,
      timeZone: initialTimeZone,
      description: metadata?.description || '',
      participantInput: '',
      participants: [],
      invitationMessage: '',
      reminders: defaultReminders,
    },
  });

  return roomForm;
};

export default useCreateNewRoomForm;
